// tagGroupsContext.js
//
// Live tag-group definitions, app-wide — the direct counterpart to workOrderStatusesContext.js,
// built the same way and for the same reason.
//
// Two separate problems this solves, which looked like one:
//
// 1. A work order stores its tags DENORMALIZED — { groupName, label, color } copied onto the
//    document when the tag was applied. So editing a tag's color in Settings changed the tagGroups
//    document while every existing work order kept rendering the color frozen into its own copy.
//    Reloading never helped, because the stale color was in the work order itself: 13 tag
//    instances across live work orders were still painting red/teal after the group had been
//    changed to orange. resolveTagColor below fixes that by treating the stored copy as an
//    IDENTITY (which group, which option) and taking the color from the current definition —
//    exactly how status colors have always worked, which is why those update and tags didn't.
//
// 2. Tag groups were fetched with a one-shot getDocs in eight separate components, each on mount.
//    Even once colors resolve correctly, an already-open screen wouldn't see an edit made
//    elsewhere until it remounted — the "close the window and reopen it" problem. onSnapshot here
//    means one live subscription for the whole app instead of eight independently stale copies.
//
// The stored color is still honored as a fallback, so a tag whose group or option has since been
// deleted keeps rendering the color it had rather than vanishing into grey.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

const TagGroupsContext = createContext(null);

// Dual query (primary companyId + participatingCompanyIds), merged by id — Firestore can't OR an
// `in` filter against an `array-contains-any` filter in one query, and a tag group shared with a
// second company has to be visible from both. Same shape as getAllTagGroups, just live.
export function TagGroupsProvider({ companyIds, children }) {
  const [tagGroups, setTagGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Joined to a primitive so a new array identity on every parent render doesn't tear down and
  // rebuild both listeners continuously.
  const companyIdsKey = (companyIds || []).join(',');

  useEffect(() => {
    const ids = (companyIds || []).filter(Boolean);
    if (ids.length === 0) {
      setTagGroups([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const ref = collection(db, 'tagGroups');

    let primaryDocs = [];
    let participatingDocs = [];
    let primaryReady = false;
    let participatingReady = false;
    const emit = () => {
      if (!primaryReady || !participatingReady) return;
      const byId = new Map(primaryDocs.map((d) => [d.id, d]));
      participatingDocs.forEach((d) => { if (!byId.has(d.id)) byId.set(d.id, d); });
      const merged = Array.from(byId.values());
      merged.sort((a, b) => (a.order || 0) - (b.order || 0));
      setTagGroups(merged);
      setLoading(false);
    };

    const unsubPrimary = onSnapshot(
      query(ref, where('companyId', 'in', ids)),
      (snap) => { primaryDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() })); primaryReady = true; emit(); },
      (error) => { console.error('Tag groups subscription failed:', error); primaryDocs = []; primaryReady = true; emit(); }
    );
    const unsubParticipating = onSnapshot(
      query(ref, where('participatingCompanyIds', 'array-contains-any', ids)),
      (snap) => { participatingDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() })); participatingReady = true; emit(); },
      (error) => { console.error('Tag groups (participating) subscription failed:', error); participatingDocs = []; participatingReady = true; emit(); }
    );

    return () => { unsubPrimary(); unsubParticipating(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyIdsKey]);

  const value = useMemo(() => {
    // "groupName|label" -> current color, rebuilt whenever the live definitions change.
    const colorByTag = new Map();
    tagGroups.forEach((g) => {
      (g.options || []).forEach((opt) => {
        if (g.name && opt && opt.label) colorByTag.set(g.name + '|' + opt.label, opt.color);
      });
    });
    // A tag stored on a work order names its group and option; the color comes from the CURRENT
    // definition. Falls back to whatever was stored when that option no longer exists (a deleted
    // tag keeps its old color rather than turning grey).
    const resolveTagColor = (tag) => {
      if (!tag) return null;
      const current = colorByTag.get(tag.groupName + '|' + tag.label);
      return current || tag.color || null;
    };
    return { tagGroups, loading, resolveTagColor };
  }, [tagGroups, loading]);

  return <TagGroupsContext.Provider value={value}>{children}</TagGroupsContext.Provider>;
}

// Safe outside a provider (e.g. the customer portal, which never mounts one): falls back to the
// color stored on the tag, i.e. exactly today's behavior, rather than throwing.
export function useTagGroups() {
  return useContext(TagGroupsContext) || {
    tagGroups: [],
    loading: false,
    resolveTagColor: (tag) => (tag && tag.color) || null
  };
}
